import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  CircularProgress,
  Box,
  alpha
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";

const ConfirmationDialog = ({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText,
  loading,
  confirmColor = "error"
}) => {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      aria-labelledby='alert-dialog-title'
      aria-describedby='alert-dialog-description'
      maxWidth='sm'
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          boxShadow: "0 12px 40px rgba(0, 0, 0, 0.18)",
          border: (theme) =>
            `1px solid ${alpha(theme.palette.warning.main, 0.3)}`,
          maxWidth: { xs: "95%", sm: 520 },
          minWidth: { xs: "90%", sm: 420 },
          position: "relative",
          overflow: "hidden"
        }
      }}
    >
      <Box
        sx={{
          height: 6,
          borderTopLeftRadius: 2,
          borderTopRightRadius: 2
        }}
      />

      <DialogTitle
        id='alert-dialog-title'
        sx={{
          fontWeight: 700,
          fontSize: { xs: "1.25rem", sm: "1.5rem" },
          py: 2.5,
          px: 3,
          display: "flex",
          alignItems: "flex-start",
          gap: 2,
          color: "text.primary"
        }}
      >
        <WarningAmberIcon
          sx={{
            fontSize: { xs: 32, sm: 36 },
            color: "warning.main",
            mt: 0.5,
            flexShrink: 0,
            animation: "pulse-icon 2s ease-in-out infinite",
            "@keyframes pulse-icon": {
              "0%, 100%": { transform: "scale(1)" },
              "50%": { transform: "scale(1.08)" }
            }
          }}
        />
        <Box sx={{ flex: 1 }}>
          {title}
          {/* 关闭按钮 - 无障碍优化 */}
        </Box>
      </DialogTitle>

      <DialogContent sx={{ px: 3, py: 2 }}>
        <DialogContentText
          id='alert-dialog-description'
          sx={{
            fontSize: { xs: "1rem", sm: "1.0625rem" },
            lineHeight: 1.65,
            color: "text.primary",
            fontWeight: 400,
            "& strong": {
              color: "error.main",
              fontWeight: 600
            },
            "& em": {
              fontStyle: "normal",
              fontWeight: 600,
              color: "primary.main"
            }
          }}
        >
          {message}
        </DialogContentText>
      </DialogContent>

      <DialogActions
        sx={{
          p: 2.5,
          pt: 1.5,
          borderTop: "1px solid",
          borderColor: "divider",
          gap: 1.5,
          flexDirection: { xs: "column-reverse", sm: "row" },
          "& > *": { minWidth: { xs: "100%", sm: "auto" } }
        }}
      >
        <Button
          onClick={onClose}
          variant='outlined'
          size='large'
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            fontSize: "1rem",
            py: 1.1,
            borderColor: "grey.300",
            "&:hover": {
              borderColor: "primary.main",
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.06),
              boxShadow: "0 2px 6px rgba(0,0,0,0.05)"
            },
            transition: "all 0.2s ease"
          }}
        >
          Cancel
        </Button>
        <Button
          onClick={onConfirm}
          variant='contained'
          size='large'
          autoFocus
          disabled={loading}
          sx={{
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1.0625rem",
            py: 1.2,
            px: 4,
            position: "relative",
            bgcolor: confirmColor === "error" ? "error.main" : "success.main",
            "&:hover": {
              bgcolor: confirmColor === "error" ? "error.dark" : "success.dark",
              boxShadow:
                confirmColor === "error"
                  ? "0 4px 16px rgba(244, 67, 54, 0.4)"
                  : "0 4px 16px rgba(46, 125, 50, 0.4)"
            },
            "&.Mui-disabled": {
              bgcolor:
                confirmColor === "error"
                  ? (theme) => alpha(theme.palette.error.main, 0.5)
                  : (theme) => alpha(theme.palette.success.main, 0.5),
              color: "common.white"
            },
            transition: "all 0.25s cubic-bezier(0.175, 0.885, 0.32, 1.275)",
            ...(loading && {
              minWidth: 120,
              "& .MuiCircularProgress-root": {
                color:
                  confirmColor === "error" ? "common.white" : "success.main",
                position: "absolute",
                left: "50%",
                transform: "translateX(-50%)"
              }
            })
          }}
        >
          {loading ? (
            <Box sx={{ width: 24, height: 24 }}>
              <CircularProgress size={24} thickness={4} />
            </Box>
          ) : (
            confirmText || "Confirm Action"
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmationDialog;
